import * as Sentry from '@sentry/react'

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost/api/v1'

// getAccessToken is injected by AuthProvider
let _getAccessToken = async () => null

export function setTokenProvider(fn) {
  _getAccessToken = fn
}

export function getAccessToken() {
  return _getAccessToken()
}

function withOwner(path, ownerUserId) {
  return ownerUserId ? `${path}?ownerUserId=${ownerUserId}` : path
}

async function request(path, options = {}) {
  const token = await _getAccessToken()
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  })
  const contentType = res.headers.get('content-type') || ''
  if (!res.ok) {
    const err = contentType.includes('application/json')
      ? await res.json().catch(() => ({}))
      : {}
    const error = { status: res.status, ...(err.error || {}) }
    if (res.status >= 500) {
      Sentry.captureException(new Error(`API ${res.status}: ${path}`), {
        extra: { status: res.status, code: error.code, message: error.message },
      })
    }
    throw error
  }
  if (!contentType.includes('application/json')) {
    throw { status: res.status, code: 'UNEXPECTED_RESPONSE', message: 'Server returned a non-JSON response' }
  }
  return res.json()
}

export const api = {
  // Profile
  getMe: () => request('/me'),

  // Sessions (own)
  listSessions: () => request('/sessions'),
  createSession: (data) => request('/sessions', { method: 'POST', body: JSON.stringify(data) }),
  getSession: (id) => request(`/sessions/${id}`),
  updateSession: (id, data) => request(`/sessions/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  scheduleSession: (id, scheduledAt) => request(`/sessions/${id}`, { method: 'PUT', body: JSON.stringify({ scheduledAt }) }),
  deleteSession: (id) => request(`/sessions/${id}`, { method: 'DELETE' }),
  readySession: (id) => request(`/sessions/${id}/ready`, { method: 'POST' }),
  cancelSession: (id) => request(`/sessions/${id}/cancel`, { method: 'POST' }),
  completeSession: (id) => request(`/sessions/${id}/complete`, { method: 'POST' }),

  // Team (manager view)
  listReports: () => request('/team/reports'),
  listReportSessions: (reportId) => request(`/team/reports/${reportId}/sessions`),
  getReportSession: (reportId, sessionId) => request(`/team/reports/${reportId}/sessions/${sessionId}`),
  updateReportSession: (reportId, sessionId, data) => request(`/team/reports/${reportId}/sessions/${sessionId}`, { method: 'PUT', body: JSON.stringify(data) }),
  getManagerNotes: (reportId, sessionId) => request(`/team/reports/${reportId}/sessions/${sessionId}/notes`),
  updateManagerNotes: (reportId, sessionId, notes) => request(`/team/reports/${reportId}/sessions/${sessionId}/notes`, { method: 'PUT', body: JSON.stringify({ notes }) }),
  updatePrivateNotes: (sessionId, notes) => request(`/sessions/${sessionId}`, { method: 'PUT', body: JSON.stringify({ privateNotes: notes }) }),
  teamFeed: () => request('/team/feed'),

  // Canvas
  getCanvas: () => request('/me/canvas'),
  updateCanvas: (layout) => request('/me/canvas', { method: 'PUT', body: JSON.stringify({ layout }) }),

  // Goals
  listGoals: (ownerUserId = null) => request(withOwner('/goals', ownerUserId)),
  createGoal: (body) => request('/goals', { method: 'POST', body: JSON.stringify(body) }),
  getGoal: (goalId, ownerUserId = null) => request(withOwner(`/goals/${goalId}`, ownerUserId)),
  updateGoal: (goalId, body, ownerUserId = null) => request(withOwner(`/goals/${goalId}`, ownerUserId), { method: 'PUT', body: JSON.stringify(body) }),
  deleteGoal: (goalId, ownerUserId = null) => request(withOwner(`/goals/${goalId}`, ownerUserId), { method: 'DELETE' }),
  activateGoal: (goalId, ownerUserId = null) => request(withOwner(`/goals/${goalId}/activate`, ownerUserId), { method: 'POST' }),
  achieveGoal: (goalId, ownerUserId = null) => request(withOwner(`/goals/${goalId}/achieve`, ownerUserId), { method: 'POST' }),
  dropGoal: (goalId, droppedReason, ownerUserId = null) => request(withOwner(`/goals/${goalId}/drop`, ownerUserId), { method: 'POST', body: JSON.stringify({ droppedReason }) }),
  reopenGoal: (goalId, ownerUserId = null) => request(withOwner(`/goals/${goalId}/reopen`, ownerUserId), { method: 'POST' }),
  updateGoalProgress: (goalId, progress, source = 'goals-page', ownerUserId = null) => request(withOwner(`/goals/${goalId}/progress`, ownerUserId), { method: 'PUT', body: JSON.stringify({ progress, source }) }),
  toggleMilestone: (goalId, milestoneId, source = 'goals-page', ownerUserId = null) => request(withOwner(`/goals/${goalId}/milestones/${milestoneId}`, ownerUserId), { method: 'PUT', body: JSON.stringify({ source }) }),
  listComments: (goalId, ownerUserId = null) => request(withOwner(`/goals/${goalId}/comments`, ownerUserId)),
  createComment: (goalId, body, ownerUserId = null) => request(withOwner(`/goals/${goalId}/comments`, ownerUserId), { method: 'POST', body: JSON.stringify({ body }) }),
  deleteComment: (goalId, commentId, ownerUserId = null) => request(withOwner(`/goals/${goalId}/comments/${commentId}`, ownerUserId), { method: 'DELETE' }),

  // Org
  addReport: (email) => request('/org/reports', { method: 'POST', body: JSON.stringify({ reportEmail: email }) }),
  updateReportTags: (reportId, tags) => request(`/org/reports/${reportId}/tags`, { method: 'PATCH', body: JSON.stringify({ tags }) }),
  removeReport: (reportId) => request(`/org/reports/${reportId}`, { method: 'DELETE' }),
  removeMyManager: () => request('/org/my-manager', { method: 'DELETE' }),
  getMyManager: () => request('/org/my-manager'),

  // Invitations (report approval flow)
  getInvitations: () => request('/me/invitations'),
  approveInvitation: (managerId) => request(`/me/invitations/${managerId}/approve`, { method: 'POST' }),
  declineInvitation: (managerId) => request(`/me/invitations/${managerId}/decline`, { method: 'POST' }),

  // Feedback
  submitFeedback: (body) => request('/feedback', { method: 'POST', body: JSON.stringify(body) }),

  // Deletion (GDPR right-to-erasure)
  requestDeletion: () => request('/me/deletion', { method: 'POST' }),
  cancelDeletion: () => request('/me/deletion', { method: 'DELETE' }),

  // Passkeys
  listPasskeys: () => request('/me/passkeys'),

  // Export (GDPR right to data portability)
  requestExport: () => request('/me/export', { method: 'POST' }),
  getExport: () => request('/me/export'),

  // Admin — overview
  adminOverview: (period = '30d') => request(`/admin/overview?period=${period}`),
  adminAlerts: () => request('/admin/alerts'),

  // Admin — users
  adminListUsers: ({ search = '', status = 'all', team = 'all', page = 1, limit = 50 } = {}) => {
    const params = new URLSearchParams({ search, status, team, page, limit })
    return request(`/admin/users?${params}`)
  },
  adminGetUser: (userId) => request(`/admin/users/${userId}`),
  adminUpdateUser: (userId, body) => request(`/admin/users/${userId}`, { method: 'PUT', body: JSON.stringify(body) }),
  adminInviteUser: (body) => request('/admin/users/invite', { method: 'POST', body: JSON.stringify(body) }),
  adminBulkInvite: (csv) => request('/admin/users/invite/bulk', { method: 'POST', body: JSON.stringify({ csv }) }),
  adminDeactivateUser: (userId) => request(`/admin/users/${userId}/deactivate`, { method: 'POST' }),
  adminReactivateUser: (userId) => request(`/admin/users/${userId}/reactivate`, { method: 'POST' }),
  adminResendInvite: (userId) => request(`/admin/users/${userId}/resend-invite`, { method: 'POST' }),
  adminReassignUser: (userId, newManagerId) => request(`/admin/users/${userId}/reassign`, { method: 'PUT', body: JSON.stringify({ newManagerId }) }),

  // Admin — org
  adminOrgTree: () => request('/admin/org/tree'),
  adminAssignManager: (userId, managerId) => request('/admin/org/assign', { method: 'PUT', body: JSON.stringify({ userId, managerId }) }),
  adminOrphanUsers: () => request('/admin/org/orphans'),
  adminTeams: () => request('/admin/org/teams'),

  // Admin — analytics
  adminAdoption: (period = '30d') => request(`/admin/analytics/adoption?period=${period}`),
  adminPrepRate: (period = '30d') => request(`/admin/analytics/prep-rate?period=${period}`),
  adminSessionsAnalytics: (period = '30d') => request(`/admin/analytics/sessions?period=${period}`),
  adminCardPopularity: (period = '30d') => request(`/admin/analytics/cards?period=${period}`),
  adminHeatmap: (period = '30d') => request(`/admin/analytics/heatmap?period=${period}`),

  // Admin — settings
  adminSettings: () => request('/admin/settings'),
  adminUpdateSettings: (body) => request('/admin/settings', { method: 'PUT', body: JSON.stringify(body) }),
  adminListAdmins: () => request('/admin/admins'),
  adminAddAdmin: (email) => request('/admin/admins', { method: 'POST', body: JSON.stringify({ email }) }),
  adminRemoveAdmin: (userId) => request(`/admin/admins/${userId}`, { method: 'DELETE' }),

  // Auth hints — unauthenticated, called from the login page before the user
  // has a session. Both endpoints fail soft (the backend returns benign
  // defaults for unknown emails) so a network miss collapses to "no SSO".
  //
  // authChallenge must be awaited before authDiscovery: it sets the short-
  // lived HttpOnly cookie that /auth/discovery requires (SEC-032). The
  // response body is intentionally empty; we only care about the Set-Cookie
  // side-effect. Cookies flow same-origin via CloudFront's /api/* proxy.
  authChallenge: async () => {
    try {
      await fetch(`${API_BASE}/auth/challenge`, { method: 'GET' })
    } catch (_) {
      // Best-effort; a network error here means discovery will return the
      // "no SSO" empty shape, and the login page falls through to native
      // auth. No user-visible error.
    }
  },
  authDiscovery: (email, redirectUri) => {
    const qs = new URLSearchParams({ email })
    if (redirectUri) qs.set('redirect_uri', redirectUri)
    return request(`/auth/discovery?${qs.toString()}`)
  },
  authPolicy: (email) => request(`/auth/policy?email=${encodeURIComponent(email)}`),
}
